import { useCallback, useState } from "react";

import { Select, Autocomplete, Button, AutocompleteSearchData, SelectData, Spinner } from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Container, Modal } from 'react-bootstrap';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useTranslation } from "react-i18next";

import { searchUser } from "../api/User/UserApi";
import { ReactComponent as DyslexicIcon } from "../assets/images/DyslexicIcon.svg";
import { ReactComponent as ImparedIcon } from "../assets/images/ImparedIcon.svg";
import { ReactComponent as NonDyslexicIcon } from "../assets/images/Non-dyslexicIcon.svg";
import { ReactComponent as NonImparedIcon } from "../assets/images/Non-imparedIcon.svg";
import { useAuth } from "../hooks";
import { UserSearch, User } from "../models/User";
import { dashboardRoute } from "../routes/routes";

interface UserCodeBook extends CodeBook {
    userRoles: CodeBook[];
};

export const UserLoginBox = () => {
    const { t } = useTranslation();

    const auth = useAuth();

    const [showModal, setShowModal] = useState<boolean>(false);
    const [userList, setUserList] = useState<UserCodeBook[]>([]);
    const [user, setUser] = useState<UserCodeBook | undefined>();
    const [userRoleId, setUserRoleId] = useState<number | undefined>();

    const [showAccessibilityModal, setShowAccessibilityModal] = useState<boolean>(false);
    const [impared, setImpared] = useState<boolean>(false);
    const [dyslexic, setDyslexic] = useState<boolean>(false);
    const [currentFontSize, setCurrentFontSize] = useState<number>(100);


    // ACCESSIBILITY
    const setImpairedStyle = useCallback(() => {
        setImpared(true);
        setDyslexic(false)
        document.documentElement.classList.add("theme-vision-impaired");
        document.documentElement.classList.remove("theme-default");
    }, []);
    const removeImpairedStyle = useCallback(() => {
        setImpared(false);
        document.documentElement.classList.add("theme-default");
        document.documentElement.classList.remove("theme-vision-impaired");
    }, []);
    const setDyslexicStyle = useCallback(() => {
        setImpared(false);
        setDyslexic(true)
        document.body.classList.add("dyslexic");
        document.documentElement.classList.add("theme-default");
        document.documentElement.classList.remove("theme-vision-impaired");
    }, []);
    const removeDyslexicStyle = useCallback(() => {
        setDyslexic(false);
        document.body.classList.remove("dyslexic");
    }, []);
    const largerFont = () => {
        if (currentFontSize < 120) {
            setCurrentFontSize(currentFontSize + 5);
            document.documentElement.style.fontSize = currentFontSize + "%";
        }
    };
    const defaultFont = () => {
        setCurrentFontSize(100);
        document.documentElement.style.fontSize = "100%";
    };
    const smallerFont = () => {
        if (currentFontSize > 80) {
            setCurrentFontSize(currentFontSize - 5);
            document.documentElement.style.fontSize = currentFontSize - 5 + "%";
        }
    };
    // AUTHORIZATION
    const getUserCodeBookArray = (userArray?: User[]): UserCodeBook[] => {
        let users = userArray ?? [];
        let result = [] as UserCodeBook[];
        users.map(val => result.push({
            id: val.id,
            name: val.lastName + ", " + val.firstName + " (" + val.oib + ")",
            userRoles: val.userRoles.map(v => { return { id: v.id!, name: v.role.name } }),
        }));
        return result;
    };
    const handleRoleChangeNumber = async (roleId: number) => {
        await auth.roleChange(roleId);
        window.location.replace(dashboardRoute);
    };
    const handleUserSearch = useCallback(async (search: AutocompleteSearchData) => {
        if (search.text && search.text.length >= 5) {
            const userSearch: UserSearch = {
                oib: search.text,
            };
            const result = await searchUser(userSearch);
            const transResult = getUserCodeBookArray(result.results);
            setUserList(transResult);
            return {
                keyProp: "id",
                valueProp: "id",
                textProp: "name",
                data: transResult,
            } as SelectData<UserCodeBook>;
        }
        return {
            keyProp: "id",
            valueProp: "id",
            textProp: "name"
        } as SelectData<UserCodeBook>;
    }, []
    );
    const handleUserChange = (id: number) => {
        setUser(userList.find(val => val.id === id));
    };
    const handleUserRoleChange = (id: number) => {
        setUserRoleId(!isNaN(id) ? id : undefined);
    };
    const handleImpersonateClick = async () => {
        let urId = userRoleId;
        if (urId && !isNaN(urId)) {
            clearImpersonationData();
            auth.impersonate(urId);
        }
    };

    const handleLogOut = async () => {
        // if we are impersonating, logout means unimpersonate.
        if (auth.currentRole?.impersonator) {
            await auth.unimpersonate();
            window.location.replace(dashboardRoute);
        }
        else
            await auth.logout();
    };

    const UserName = () => {
        if (!auth.user) return null;
        return (
            <>
                {(auth.currentRole?.impersonator) &&
                    <>
                        <span className="text-danger">{auth.currentRole?.impersonator.user.firstName} </span>
                        <span className="text-danger"> {auth.currentRole?.impersonator.user.lastName}  - </span>
                        <span className="text-danger"> {auth.currentRole?.impersonator.user.oib} - </span>
                        <span className="text-danger"><b> {auth.currentRole?.impersonator.name}</b> : </span>
                        <br />
                    </>
                }
                <>
                    <span> {auth.user.firstName} </span>
                    <span> {auth.user.lastName}  - </span>
                    <span> {auth.user.oib} - </span>
                    <b> {auth.currentRole?.name}</b>
                </>
            </>
        );
    };

    const clearImpersonationData = () => {
        setShowModal(false);
        setUserRoleId(undefined);
        setUser(undefined);
        setUserList([]);
    };

    if (auth.isLoading) return <Spinner show={true} />;
    return (
        <>
            <Nav className="me-auto">
                <NavDropdown
                    title={UserName()}
                    id="basic-nav-dropdown"
                >
                    <NavDropdown.Header>{t("login.userRoles")}</NavDropdown.Header>
                    <NavDropdown.ItemText>
                        <Select
                            name={auth.currentRole?.name}
                            value={auth.currentRole?.id}
                            onChange={(e) => handleRoleChangeNumber(e.target.value)}
                            selectData={codeBookToSelectData(auth.roles)}
                            readonly={(auth.roles.length ?? 1) === 1}
                        />
                    </NavDropdown.ItemText>
                    <NavDropdown.Divider />
                    {(auth.currentRole?.canImpersonate) &&
                        <>
                            <NavDropdown.Item onClick={() => setShowModal(true)}>{t("login.impersonate")}</NavDropdown.Item>
                            <NavDropdown.Divider />
                            <Modal
                                size="lg"
                                backdrop="static"
                                centered
                                animation={false}
                                show={showModal}
                                onConfirm={clearImpersonationData}
                                onClose={clearImpersonationData}
                                onHide={clearImpersonationData}
                            >
                                <Modal.Header>
                                    {t("common.user.pageNameDetails")}
                                </Modal.Header>
                                <Modal.Body>
                                    <Container>
                                        <Row>
                                            <Col md={6}>
                                                <Autocomplete
                                                    label={t("common.user.oib")}
                                                    minimumInputLength={5}
                                                    placeholder={t("common.user.oib")}
                                                    value={user?.id}
                                                    getOptions={handleUserSearch}
                                                    onChange={(e) => { console.log(e.type); if (e.type === "click") handleUserChange(e.target.value); }}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Select
                                                    label={t("common.user.roles")}
                                                    name="userRole"
                                                    value={userRoleId}
                                                    placeholder={t("common.user.roles")}
                                                    onChange={(e) => handleUserRoleChange(e.target.value)}
                                                    selectData={codeBookToSelectData(user?.userRoles)}
                                                    disabled={!user?.id}
                                                />
                                            </Col>
                                        </Row>
                                    </Container>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button onClick={clearImpersonationData}
                                        size="sm"
                                        outline={true}>
                                        {t("common.quit")}
                                    </Button>
                                    <Button onClick={handleImpersonateClick}
                                        size="sm"
                                        disabled={!userRoleId}
                                    >
                                        {t("login.impersonate")}
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                        </>
                    }
                    {auth.userInfo?.IDP?.toLowerCase() !== "nias" &&
                        (<>
                            <NavDropdown.Item onClick={(e) => setShowAccessibilityModal(true)}>Postavke prikaza</NavDropdown.Item>
                            <NavDropdown.Divider />
                        </>
                        )
                    }
                    <NavDropdown.Item onClick={(e) => handleLogOut()}>{t("login.logoutButton")}</NavDropdown.Item>
                </NavDropdown>
            </Nav>
            <Modal
                backdrop="static"
                centered
                animation={false}
                show={showAccessibilityModal}

            >
                <Modal.Header>
                    {t("accessibility.managmenet")}
                </Modal.Header>
                <Modal.Body>
                    <Container>
                        <Row className="m-3">
                            <Col md={12}>
                                {impared
                                    ? <Button onClick={(e) => removeImpairedStyle()} size="sm" outline icon={<ImparedIcon />} className="full-width">{t("accessibility.removeImpairedStyle")}</Button>
                                    : <Button onClick={(e) => setImpairedStyle()} size="sm" outline icon={<NonImparedIcon />} className="full-width">{t("accessibility.setImpairedStyle")}</Button>
                                }
                            </Col>
                        </Row>
                        <Row className="m-3">
                            <Col md={12}>
                                {dyslexic
                                    ? <Button onClick={(e) => removeDyslexicStyle()} size="sm" outline icon={<NonDyslexicIcon />} className="full-width">{t("accessibility.removeDyslexicStyle")}</Button>
                                    : <Button onClick={(e) => setDyslexicStyle()} size="sm" outline icon={<DyslexicIcon />} className="full-width">{t("accessibility.setDyslexicStyle")}</Button>
                                }
                            </Col>
                        </Row>
                        <Row className="m-4" class="text-center">
                            <Col md={3}>
                                <Button onClick={(e) => smallerFont()} size="sm" outline > - </Button>
                            </Col>
                            <Col md={4} class="text-center">
                                <Button onClick={(e) => defaultFont()}
                                    size="sm"
                                    outline={!(currentFontSize === 80 || currentFontSize === 120)}
                                >
                                    {currentFontSize}
                                </Button>
                            </Col>
                            <Col md={3} class="text-center">
                                <Button onClick={(e) => largerFont()} size="sm" outline> + </Button>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={(e) => setShowAccessibilityModal(false)} size="sm">{t("common.close")}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
